import { generateRandomId } from '@sparklayer-io/spark-dash-ui/lib/random';
import type { CountryCode } from '../../utils/countries-iso3166';
import type { ShippingMethodBuilderState, ShippingMethodBuilderValidated } from './schemas';
import type {
  ShippingBandApiResponse,
  ShippingMethodApiBase,
  ShippingMethodApiResponse,
} from './types/shipping-method';

function transformCostToApi(
  cost: ShippingMethodBuilderValidated['bands'][0]['cost'],
): ShippingBandApiResponse['cost'] {
  switch (cost.type) {
    case 'free':
      return { type: 'free' };
    case 'percent_net_total':
      return { type: 'percent_net_total', percentage_charge: cost.percentageCharge };
    case 'fixed_price':
      return { type: 'fixed_price', price: cost.price };
    case 'custom':
      return { type: 'custom', cost_language_string: cost.costLanguageString };
  }
}

function transformRequirementToApi(
  requirement: ShippingMethodBuilderValidated['bands'][0]['requirement'],
): ShippingBandApiResponse['requirement'] {
  switch (requirement.type) {
    case 'none':
      return { type: 'none' };
    case 'weight':
      return { type: 'weight', min_grams: requirement.minGrams, max_grams: requirement.maxGrams };
    case 'net_total':
      return {
        type: 'net_total',
        min_total: requirement.minTotal,
        max_total: requirement.maxTotal,
      };
  }
}

export function transformShippingMethodToApi(
  state: ShippingMethodBuilderValidated,
): ShippingMethodApiBase {
  return {
    ...state,
    title: state.name,
    groups: state.customerGroups,
    bands: state.bands.map((band) => ({
      ...band,
      cost: transformCostToApi(band.cost),
      requirement: transformRequirementToApi(band.requirement),
      key: undefined, // Get rid of key used only for front end
    })),
  };
}

function transformCostToState(
  cost: ShippingBandApiResponse['cost'],
): ShippingMethodBuilderState['bands'][0]['cost'] {
  switch (cost.type) {
    case 'free':
      return { type: 'free' };
    case 'percent_net_total':
      return { type: 'percent_net_total', percentageCharge: cost.percentage_charge.toString() };
    case 'fixed_price':
      return { type: 'fixed_price', price: cost.price.toString() };
    case 'custom':
      return { type: 'custom', costLanguageString: cost.cost_language_string };
  }
}

function transformRequirementToState(
  requirement: ShippingBandApiResponse['requirement'],
): ShippingMethodBuilderState['bands'][0]['requirement'] {
  switch (requirement.type) {
    case 'none':
      return { type: 'none' };
    case 'weight':
      return {
        type: 'weight',
        minGrams: requirement.min_grams?.toString() ?? '',
        maxGrams: requirement.max_grams?.toString() ?? '',
      };
    case 'net_total':
      return {
        type: 'net_total',
        minTotal: requirement.min_total?.toString() ?? '',
        maxTotal: requirement.max_total?.toString() ?? '',
      };
    case 'gross_total':
      throw new Error('Cannot edit "gross total" requirement');
  }
}

export function transformShippingMethodToState(
  data: ShippingMethodApiResponse,
): ShippingMethodBuilderState {
  return {
    ...data,
    sku: data.sku ?? '',
    priority: data.priority.toString(),
    countries: data.countries as CountryCode[],
    name: data.title,
    customerGroups: data.groups,
    bands: data.bands.map((band) => ({
      ...band,
      priority: band.priority.toString(),
      cost: transformCostToState(band.cost),
      requirement: transformRequirementToState(band.requirement),
      key: !band.sku || band.sku.length === 0 ? generateRandomId() : band.sku,
    })),
  };
}
